import { cachedAPIWrapper, getCachedAPIResponse } from "../apiCaching/apiWrapper";
import { apiConfig } from "../config/apiConfig";
import { apiCaching } from "../config/cachingConfig";
import { CleaningType } from "../mly";
import { determineCountryZip, filterFranchiseData } from "./share";

(Number.prototype as any).degreeToRadius = function (this: any): number {
    return this * (Math.PI / 180);
};

(Number.prototype as any).radiusToDegree = function (this: any): any {
    return (180 * this) / Math.PI;
};

// Define an interface for H.geo.Rect and declare H as a global interface on the window object
declare global {
    interface H {
        geo: {
            Rect: new (top: number, left: number, bottom: number, right: number) => HGeoRect;
        };
    }

    interface HGeoRect {
        containsLatLng(lat: number, lng: number): boolean;
    }
}
export class NearbyLocations {
    private readonly units: string;
    private readonly franchiseLimit: number;
    private readonly countryCode: string;
    private readonly singleCallBackFcn: Function;
    private readonly multiCallBackFcn: Function;
    private readonly errorCallBackFcn: Function;
    public cleaningType: CleaningType;
    public bookingPayload: any;
    public zipCode : string | number | undefined;
    public isLeadContactApi : boolean = false;
    constructor(units: string='miles', franchiseLimit: number = 50, singleCallBackFcn = () => { }, multiCallBackFcn = () => { }, errorCallBackFcn = () => { }, bookingPayload :any={},zipCode :number | string | undefined=undefined) {
        // Setting default value, with room for updation in future
        this.units = units;
        this.franchiseLimit = franchiseLimit;
        this.countryCode = (document.getElementById("countryCode") as HTMLInputElement)?.value.toLowerCase();
        this.loadHereMapScripts();
        this.singleCallBackFcn = singleCallBackFcn;
        this.multiCallBackFcn = multiCallBackFcn;
        this.errorCallBackFcn = errorCallBackFcn;
        this.cleaningType = new CleaningType();
        this.bookingPayload = bookingPayload;
        this.zipCode = zipCode;
    }

    loadHereMapScripts() {
        const isScriptLoaded = document.getElementById('mapsjs-core-js');
        if (!isScriptLoaded) {
            const fcn = (url: string, id: string) => {
                const elem = document.createElement("script");
                elem.async = false;
                elem.src = url;
                elem.id = id;
                document.head.appendChild(elem);
            }

            fcn('https://js.api.here.com/v3/3.1/mapsjs-core.js', 'mapsjs-core-js');
            fcn('https://js.api.here.com/v3/3.1/mapsjs-ui.js', 'mapsjs-ui-js');
        }
    }
    convertToKM(limit:number, units:string) {
        const factor = 1.60934;
        if (units.includes('miles') ) {
            return Math.round(limit * factor);
        }
        return limit;
    }
    getAllLocations() {
        //Pass serviceID if exists
        const serviceId = sessionStorage.getItem('serviceIdValue');
        let url = apiConfig.WebLocationsAPI;
        if (serviceId) {
            url += `/${serviceId}`;
            sessionStorage.removeItem('serviceIdValue');
        }
        return cachedAPIWrapper(url);
    }

     async searchNearbyLocations(searchQuery: string) {
        let countryZip = determineCountryZip(searchQuery);
        let geoCountry = "USA,PRI";
        if(countryZip ==='ca'){
            geoCountry = "CAN";
        }
        const apiUrl = `${apiConfig.geoCodeSearchApiUrl}?apiKey=${process.env.JS_Heremap_API_KEY}&q=${searchQuery.trim()}&in=countryCode:${geoCountry}`;

        // Promises to handle asynchronous operations
        return getCachedAPIResponse(apiUrl, apiCaching.HereMapConfig, false)
            .then(data => {
                if (data?.items && data.items.length !== 0) {
                    const position = data.items[0].position;
                    const rangeConvert = this.convertToKM(this.franchiseLimit, this.units);
                    sessionStorage.setItem('distanceTo', JSON.stringify(position));

                    const bbox = this.getBoundingBox(position.lat, position.lng, rangeConvert);
                    const hBBox = new H.geo.Rect(bbox[0], bbox[1], bbox[2], bbox[3]);
                     
                return this.getAllLocations()
                        .then(response => {
                            const result = filterFranchiseData(response);
                            const filteredData = this.filterResults(hBBox, result, -1);
                            return filteredData;
                        })
                        .catch(err => {
                            console.error("Facing this error when calling Web Locations API", err);
                            return null;
                        });
                } else {
                    console.log("Error: No items found in data.");
                    return null;
                }
            })
            .catch(err => {
                console.error("Facing this error when calling Geo Code API", err);
                return null;
            });
    }


    filterResults(bbox: HGeoRect, franchiseDetails: any[], pinnedWeblocationId: any) {
        const distanceTo: any = JSON.parse(sessionStorage.getItem('distanceTo') as string);
        let currFranchiseDetails: any = [];
        franchiseDetails.forEach((fr) => {
            if (bbox.containsLatLng(fr.latitude, fr.longitude) || pinnedWeblocationId === fr.franchiseWebLocationId) {
                fr.distance = this.getDistanceFromLatLon(distanceTo.lat, distanceTo.lng, fr.latitude, fr.longitude);
                currFranchiseDetails.push(fr);
            }
        });
        if (currFranchiseDetails.length === 1) {
            console.log("Only one location found within range");
            this.singleCallBackFcn();
            return currFranchiseDetails;
        } else if (currFranchiseDetails.length > 1) {
            console.log("Multiple locations found within range");
            this.multiCallBackFcn();
            return currFranchiseDetails;
        } else {
            console.error(`Could not find a location within ${this.franchiseLimit} ${this.units} range`);
            this.errorCallBackFcn();
            return currFranchiseDetails;
        }

    }

    getDistanceFromLatLon(lat1: any, lon1: any, lat2: any, lon2: any) {
        if ((lat1 == lat2) && (lon1 == lon2)) {
            return 0;
        }
        const radlat1 = Math.PI * lat1 / 180;
        const radlat2 = Math.PI * lat2 / 180;
        const theta = lon1 - lon2;
        const radtheta = (Math.PI * theta) / 180;
        let dist = Math.sin(radlat1) * Math.sin(radlat2) + Math.cos(radlat1) * Math.cos(radlat2) * Math.cos(radtheta);
        if (dist > 1) {
            dist = 1;
        }
        dist = Math.acos(dist);
        dist = dist * 180 / Math.PI * 60 * 1.1515;
        if (this.units === "km") {
            dist = dist * 1.609344
        }
        return dist;
    }

    getBoundingBox(fsLatitude: any, fsLongitude: any, fiDistanceInKM: any): any[] {

        if (fiDistanceInKM == null || fiDistanceInKM == undefined || fiDistanceInKM == 0)
            fiDistanceInKM = 1;

        let MIN_LAT: any, MAX_LAT: any, MIN_LON: any, MAX_LON: any, ldEarthRadius: any, ldDistanceInRadius: any, lsLatitudeInDegree: any, lsLongitudeInDegree: any,
            lsLatitudeInRadius: any, lsLongitudeInRadius: any, lsMinLatitude: any, lsMaxLatitude: any, lsMinLongitude: any, lsMaxLongitude: any, deltaLon: any;

        // coordinate limits
        MIN_LAT = (-90 as any).degreeToRadius();
        MAX_LAT = (90 as any).degreeToRadius();
        MIN_LON = (-180 as any).degreeToRadius();
        MAX_LON = (180 as any).degreeToRadius();

        // Earth's radius (km)
        ldEarthRadius = 6378.1;

        // angular distance in radians on a great circle
        ldDistanceInRadius = fiDistanceInKM / ldEarthRadius;

        // center point coordinates (deg)
        lsLatitudeInDegree = fsLatitude;
        lsLongitudeInDegree = fsLongitude;

        // center point coordinates (rad)
        lsLatitudeInRadius = lsLatitudeInDegree.degreeToRadius();
        lsLongitudeInRadius = lsLongitudeInDegree.degreeToRadius();

        // minimum and maximum latitudes for given distance
        lsMinLatitude = lsLatitudeInRadius - ldDistanceInRadius;
        lsMaxLatitude = lsLatitudeInRadius + ldDistanceInRadius;

        // minimum and maximum longitudes for given distance
        lsMinLongitude = undefined;
        lsMaxLongitude = undefined;

        // define deltaLon to help determine min and max longitudes
        deltaLon = Math.asin(Math.sin(ldDistanceInRadius) / Math.cos(lsLatitudeInRadius));

        if (lsMinLatitude > MIN_LAT && lsMaxLatitude < MAX_LAT) {
            lsMinLongitude = lsLongitudeInRadius - deltaLon;
            lsMaxLongitude = lsLongitudeInRadius + deltaLon;
            if (lsMinLongitude < MIN_LON) {
                lsMinLongitude = lsMinLongitude + 2 * Math.PI;
            }
            if (lsMaxLongitude > MAX_LON) {
                lsMaxLongitude = lsMaxLongitude - 2 * Math.PI;
            }
        }

        // a pole is within the given distance
        else {
            lsMinLatitude = Math.max(lsMinLatitude, MIN_LAT);
            lsMaxLatitude = Math.min(lsMaxLatitude, MAX_LAT);
            lsMinLongitude = MIN_LON;
            lsMaxLongitude = MAX_LON;
        }

        return [
            lsMinLatitude.radiusToDegree(),
            lsMinLongitude.radiusToDegree(),
            lsMaxLatitude.radiusToDegree(),
            lsMaxLongitude.radiusToDegree()
        ];
    }

}

